import React, { useEffect, useState } from "react";
import {
  Copy, UserPlus, Users, Mail, Shield, ChevronRight,
  Pencil, Lock, Unlock, Trash2, X, Save, Send,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { PageHeader, Spinner, EmptyState } from "../components/UI";
import { useAuth } from "../contexts/AuthContext";
import { fmtDateShort } from "../lib/utils";

const ROLE_BADGE = {
  admin: "bg-brand-ink text-white",
  brand: "bg-brand-gold text-brand-ink",
  manager: "bg-stage-proposalBg text-stage-proposalFg",
  rep: "bg-stage-leadBg text-stage-leadFg",
  user: "bg-brand-muted text-brand-ink2",
};

// Which roles each inviter can target. Mirrors the backend allowed_targets.
const INVITE_TARGETS_BY_ROLE = {
  admin: ["admin", "brand", "manager", "rep"],
  brand: ["manager"],
  manager: ["rep"],
};

export default function Team() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [teams, setTeams] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null); // user obj or null

  // Invite form state
  const isAdmin = user?.role === "admin";
  const [inviteRole, setInviteRole] = useState(
    user ? (INVITE_TARGETS_BY_ROLE[user.role] || [null])[0] : null,
  );
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteOrgId, setInviteOrgId] = useState("");
  const [inviteOrgName, setInviteOrgName] = useState("");
  const [inviteTeamId, setInviteTeamId] = useState("");
  const [inviteTeamName, setInviteTeamName] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);

  const canInvite = ["admin", "brand", "manager"].includes(user?.role);
  const targetOptions = INVITE_TARGETS_BY_ROLE[user?.role] || [];

  const load = async () => {
    setLoading(true);
    try {
      const promises = [
        api.get("/users/scope"),
        canInvite ? api.get("/invitations") : Promise.resolve({ data: { items: [] } }),
        ["admin", "brand", "manager"].includes(user?.role)
          ? api.get("/teams") : Promise.resolve({ data: { items: [] } }),
        isAdmin ? api.get("/orgs") : Promise.resolve({ data: { items: [] } }),
      ];
      const [m, i, t, o] = await Promise.all(promises);
      setMembers(m.data.items || []);
      setInvites(i.data.items || []);
      setTeams(t.data.items || []);
      setOrgs(o.data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.role]);

  // Reset org/team selection when inviteRole changes
  useEffect(() => {
    setInviteOrgId("");
    setInviteOrgName("");
    setInviteTeamId("");
    setInviteTeamName("");
  }, [inviteRole]);

  const submitInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !inviteRole) return;
    setSendingInvite(true);
    try {
      const payload = { role: inviteRole, email: inviteEmail.trim() };
      // Per inviter role, build out the extra fields the backend expects.
      if (user?.role === "brand") {
        if (!inviteTeamName.trim()) {
          toast.error("Team name is required when inviting a manager.");
          setSendingInvite(false); return;
        }
        payload.team_name = inviteTeamName.trim();
      } else if (isAdmin) {
        if (inviteRole === "brand") {
          if (inviteOrgId) payload.org_id = inviteOrgId;
          else if (inviteOrgName.trim()) payload.org_name = inviteOrgName.trim();
          else {
            toast.error("Pick an organization or type a new one.");
            setSendingInvite(false); return;
          }
        } else if (inviteRole === "manager" || inviteRole === "rep") {
          if (!inviteOrgId) {
            toast.error("Pick the organization the new user belongs to.");
            setSendingInvite(false); return;
          }
          payload.org_id = inviteOrgId;
          if (inviteTeamId) payload.team_id = inviteTeamId;
          else if (inviteTeamName.trim()) payload.team_name = inviteTeamName.trim();
          else {
            toast.error("Pick a team or type a new team name.");
            setSendingInvite(false); return;
          }
        }
        // admin → admin needs no extra fields
      }
      const { data } = await api.post("/invitations", payload);
      const link = `${window.location.origin}/invite/${data.token}`;
      // Always keep a clipboard copy as a fallback (in case email is delayed
      // or the address turns out to be wrong).
      await navigator.clipboard.writeText(link).catch(() => {});
      if (data.email) {
        toast.success(`Invite sent to ${data.email} · link also copied to clipboard`);
      } else {
        toast.success("Invite created — link copied to clipboard");
      }
      setInviteEmail("");
      setInviteOrgId(""); setInviteOrgName("");
      setInviteTeamId(""); setInviteTeamName("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSendingInvite(false);
    }
  };

  const copyInviteLink = async (token) => {
    const link = `${window.location.origin}/invite/${token}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Invite link copied");
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  const resendInvite = async (inv) => {
    try {
      await api.post(`/invitations/${inv.id}/resend`);
      toast.success(`Re-sent invite to ${inv.email}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const reassignRep = async (rep, newTeamId) => {
    try {
      await api.post(`/users/${rep.id}/reassign`, { team_id: newTeamId });
      toast.success(`Moved ${rep.name} to a new team`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const toggleDisabled = async (m) => {
    const next = !m.is_disabled;
    try {
      await api.patch(`/users/${m.id}`, { is_disabled: next });
      toast.success(next ? `Disabled ${m.name || m.email}` : `Re-enabled ${m.name || m.email}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const deleteUser = async (m) => {
    const confirmed = window.confirm(
      `Permanently delete ${m.name || m.email}?\n\n` +
      `Their account will be removed and they'll no longer be able to sign in. ` +
      `Establishments and visits they created will stay in the system.`
    );
    if (!confirmed) return;
    try {
      await api.delete(`/users/${m.id}`);
      toast.success(`Deleted ${m.name || m.email}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) return <Spinner />;

  if (!user || user.role === "rep" || user.role === "user") {
    return (
      <div>
        <PageHeader testid="team-header" title="Team"
          subtitle="You're flying solo in your private workspace." />
        <EmptyState testid="team-rep-empty" icon={Users}
          title="No team to manage yet"
          body="Once a manager or brand owner invites you, this page will show your colleagues." />
      </div>
    );
  }

  // Filter teams by inviteOrgId when admin is inviting manager/rep
  const inviteTeamsForOrg = isAdmin && inviteOrgId
    ? teams.filter((t) => t.org_id === inviteOrgId)
    : teams;

  return (
    <div>
      <PageHeader
        testid="team-header"
        title={isAdmin ? "All users" :
              user?.role === "brand" ? `${user.org_name || "Your org"} team` :
              "Your team"}
        subtitle={
          isAdmin ? "Every account across every organization." :
          user?.role === "brand" ? "Managers and reps across your organization." :
          "Reps you manage."
        }
      />

      {canInvite && (
        <form onSubmit={submitInvite} className="card p-5 mb-4" data-testid="invite-form-card">
          <div className="grid sm:grid-cols-[auto,1fr,1fr,1fr,auto] gap-3 items-end">
            {/* Role picker (admin) or static label (others) */}
            {isAdmin ? (
              <div>
                <div className="label mb-1">Role</div>
                <select
                  data-testid="invite-role-select"
                  className="input-base"
                  value={inviteRole || ""}
                  onChange={(e) => setInviteRole(e.target.value)}
                >
                  {targetOptions.map((r) => (
                    <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="sm:hidden" />
            )}

            <div>
              <div className="label mb-1">
                {isAdmin ? "Email" : `Invite a ${inviteRole}`}
              </div>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-mutedFg" />
                <input
                  data-testid="invite-email-input"
                  type="email" required className="input-base pl-9"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="newhire@brand.com"
                />
              </div>
            </div>

            {/* Org picker — admin inviting brand/manager/rep */}
            {isAdmin && inviteRole !== "admin" && (
              <div>
                <div className="label mb-1">Organization</div>
                <select
                  data-testid="invite-org-select"
                  className="input-base"
                  value={inviteOrgId}
                  onChange={(e) => { setInviteOrgId(e.target.value); setInviteOrgName(""); setInviteTeamId(""); }}
                >
                  <option value="">— pick existing —</option>
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
                {inviteRole === "brand" && !inviteOrgId && (
                  <input
                    data-testid="invite-org-name"
                    className="input-base mt-2"
                    placeholder="…or new org name"
                    value={inviteOrgName}
                    onChange={(e) => setInviteOrgName(e.target.value)}
                  />
                )}
              </div>
            )}

            {/* Team picker — admin inviting manager/rep, or brand inviting manager */}
            {((isAdmin && (inviteRole === "manager" || inviteRole === "rep")) ||
              user?.role === "brand") && (
              <div>
                <div className="label mb-1">Team</div>
                {isAdmin ? (
                  <>
                    <select
                      data-testid="invite-team-select"
                      className="input-base"
                      value={inviteTeamId}
                      onChange={(e) => { setInviteTeamId(e.target.value); setInviteTeamName(""); }}
                      disabled={!inviteOrgId}
                    >
                      <option value="">— pick existing —</option>
                      {inviteTeamsForOrg.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                    {!inviteTeamId && inviteOrgId && (
                      <input
                        data-testid="invite-team-name"
                        className="input-base mt-2"
                        placeholder="…or new team name"
                        value={inviteTeamName}
                        onChange={(e) => setInviteTeamName(e.target.value)}
                      />
                    )}
                  </>
                ) : (
                  <input
                    data-testid="invite-team-name"
                    required className="input-base"
                    value={inviteTeamName}
                    onChange={(e) => setInviteTeamName(e.target.value)}
                    placeholder="e.g. Savannah Field Team"
                  />
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={sendingInvite}
              className="btn-primary"
              data-testid="send-invite-button"
            >
              <UserPlus size={16} /> {sendingInvite ? "Creating…" : "Send invite"}
            </button>
          </div>
          <div className="text-xs text-brand-mutedFg mt-3">
            We'll create an invite link and copy it to your clipboard — share it however you like (email, Slack, SMS).
          </div>
        </form>
      )}

      <div className="grid lg:grid-cols-[1.4fr,1fr] gap-4">
        {/* Members */}
        <div className="card p-2">
          <div className="px-3 py-3 flex items-center justify-between">
            <div className="label">Members</div>
            <div className="text-xs text-brand-mutedFg">{members.length}</div>
          </div>
          {members.length === 0 ? (
            <div className="px-3 py-8 text-sm text-brand-mutedFg text-center">No members yet.</div>
          ) : (
            <div className="divide-y divide-brand-border" data-testid="team-members-list">
              {members.map((m) => (
                <div key={m.id} className="px-3 py-3 flex items-center gap-3 flex-wrap" data-testid={`member-${m.id}`}>
                  <div className={`w-9 h-9 rounded-full ${m.is_disabled ? "bg-brand-muted text-brand-mutedFg" : "bg-brand-muted text-brand-ink"} flex items-center justify-center shrink-0`}>
                    <Shield size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium truncate ${m.is_disabled ? "text-brand-mutedFg line-through" : "text-brand-ink"}`}>
                      {m.name || m.email}
                    </div>
                    <div className="text-xs text-brand-mutedFg truncate">
                      {m.email} · {m.org_name || "—"}{m.team_name ? ` · ${m.team_name}` : ""}
                    </div>
                  </div>
                  <span className={`badge ${ROLE_BADGE[m.role] || "bg-brand-muted text-brand-ink"}`}>{m.role}</span>
                  {m.is_disabled && (
                    <span className="badge bg-stage-lostBg text-stage-lostFg" data-testid={`disabled-badge-${m.id}`}>disabled</span>
                  )}
                  {/* Brand-owner-only rep reassign select */}
                  {user?.role === "brand" && m.role === "rep" && teams.length > 1 && (
                    <select
                      className="input-base !min-h-0 py-1.5 text-xs w-44"
                      value={m.team_id || ""}
                      onChange={(e) => e.target.value && e.target.value !== m.team_id && reassignRep(m, e.target.value)}
                      data-testid={`reassign-${m.id}`}
                    >
                      {teams.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  )}
                  {/* Admin actions */}
                  {isAdmin && m.id !== user.id && (
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setEditingUser(m)}
                        className="btn-ghost !px-2 !py-1"
                        title="Edit"
                        data-testid={`edit-${m.id}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleDisabled(m)}
                        className="btn-ghost !px-2 !py-1"
                        title={m.is_disabled ? "Re-enable" : "Disable"}
                        data-testid={`toggle-disabled-${m.id}`}
                      >
                        {m.is_disabled ? <Unlock size={14} /> : <Lock size={14} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteUser(m)}
                        className="btn-ghost !px-2 !py-1 text-stage-lostFg hover:bg-stage-lostBg"
                        title="Delete"
                        data-testid={`delete-${m.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pending invites */}
        <div className="card p-2">
          <div className="px-3 py-3 flex items-center justify-between">
            <div className="label">Pending invites</div>
            <div className="text-xs text-brand-mutedFg">{invites.filter((i) => !i.accepted_at).length}</div>
          </div>
          {invites.length === 0 ? (
            <div className="px-3 py-8 text-sm text-brand-mutedFg text-center">No invites sent.</div>
          ) : (
            <div className="divide-y divide-brand-border" data-testid="invites-list">
              {invites.map((i) => (
                <div key={i.id} className="px-3 py-3" data-testid={`invite-${i.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-brand-ink text-sm">{i.email || "(no email)"}</div>
                    <span className={`badge ${ROLE_BADGE[i.role] || "bg-brand-muted text-brand-ink"}`}>{i.role}</span>
                    {i.accepted_at && <span className="badge stage-closed">accepted</span>}
                  </div>
                  <div className="text-xs text-brand-mutedFg mt-1">
                    Created {fmtDateShort(i.created_at)} · Expires {fmtDateShort(i.expires_at)}
                  </div>
                  {!i.accepted_at && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {i.email && (
                        <button
                          type="button"
                          onClick={() => resendInvite(i)}
                          className="btn-ghost text-xs"
                          data-testid={`resend-invite-${i.id}`}
                          title={`Re-send to ${i.email}`}
                        >
                          <Send size={12} /> Resend email
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => copyInviteLink(i.token)}
                        className="btn-ghost text-xs"
                        data-testid={`copy-invite-${i.id}`}
                      >
                        <Copy size={12} /> Copy link <ChevronRight size={12} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {editingUser && (
        <EditUserModal
          target={editingUser}
          orgs={orgs}
          teams={teams}
          onClose={() => setEditingUser(null)}
          onSaved={async () => { setEditingUser(null); await load(); }}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Edit user modal — admin only. Lets you change name, role, org, team.
// Disable + delete are handled by inline row buttons instead.
function EditUserModal({ target, orgs, teams, onClose, onSaved }) {
  const [name, setName] = useState(target.name || "");
  const [role, setRole] = useState(target.role || "rep");
  const [orgId, setOrgId] = useState(target.org_id || "");
  const [teamId, setTeamId] = useState(target.team_id || "");
  const [saving, setSaving] = useState(false);

  const teamsForOrg = orgId ? teams.filter((t) => t.org_id === orgId) : [];
  // Admin role has no org/team
  const needsOrg = role !== "admin";
  const needsTeam = role === "manager" || role === "rep";

  const save = async () => {
    setSaving(true);
    const patch = { name: name.trim() };
    if (role !== target.role) patch.role = role;
    if (needsOrg) patch.org_id = orgId || null;
    else patch.org_id = null;
    if (needsTeam) patch.team_id = teamId || null;
    else patch.team_id = null;
    try {
      await api.patch(`/users/${target.id}`, patch);
      toast.success("User updated");
      await onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/40 p-4"
      onClick={onClose}
      data-testid="edit-user-modal"
    >
      <div
        className="card w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="label">Editing</div>
            <div className="font-display text-xl text-brand-ink">{target.email}</div>
          </div>
          <button onClick={onClose} className="btn-ghost !px-2 !py-1" data-testid="edit-user-close">
            <X size={16} />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <div className="label mb-1">Name</div>
            <input
              data-testid="edit-user-name"
              className="input-base"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <div className="label mb-1">Role</div>
            <select
              data-testid="edit-user-role"
              className="input-base"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="admin">Admin</option>
              <option value="brand">Brand Owner</option>
              <option value="manager">Manager</option>
              <option value="rep">Rep</option>
            </select>
          </div>
          {needsOrg && (
            <div>
              <div className="label mb-1">Organization</div>
              <select
                data-testid="edit-user-org"
                className="input-base"
                value={orgId}
                onChange={(e) => { setOrgId(e.target.value); setTeamId(""); }}
              >
                <option value="">— none —</option>
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>
          )}
          {needsTeam && (
            <div>
              <div className="label mb-1">Team</div>
              <select
                data-testid="edit-user-team"
                className="input-base"
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
                disabled={!orgId}
              >
                <option value="">— none —</option>
                {teamsForOrg.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="btn-ghost" data-testid="edit-user-cancel">
            Cancel
          </button>
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="edit-user-save">
            <Save size={14} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
